import axios from "axios";
import { stringify } from "qs";

import { findCustomClientBByHostname } from "~/config/customClientBConfig";
import envConstants from "~/config/envConstants";
import { accessTokenFactory } from "~/features/login/login.slice";
import { AuthMethod } from "~/features/login/login.slice.interfaces";
import { processErrorArray } from "~/lib/helpers";

import { isAxiosError } from "./shared";

let interceptorId: number | null = null;

export const getWarehouseServiceUrl = (
  hostname: string = window.location.hostname // defaults to current hostname if not provided (e.g., for testing)
): string => {
  const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";

  const bevConfig = findCustomClientBByHostname(hostname);
  if (bevConfig?.warehouseApiUrl) {
    return bevConfig.warehouseApiUrl;
  }
  if (hostname.includes("autostoresystem") || isLocalHost) {
    return envConstants.AS_WAREHOUSE_API;
  }
  return envConstants.WAREHOUSE_API;
};

export const warehouseService = axios.create({
  baseURL: getWarehouseServiceUrl(),
  headers: {},
  params: {},
  paramsSerializer: (params) => stringify(params, { arrayFormat: "repeat" })
});

export function setupAuthInterceptor({
  authMethod,
  stateAccessToken
}: {
  authMethod: AuthMethod | null;
  stateAccessToken: string | null;
}): Promise<boolean> {
  return new Promise((resolve) => {
    // Remove the previous interceptor, if any
    if (interceptorId !== null) {
      warehouseService.interceptors.request.eject(interceptorId);
    }

    try {
      interceptorId = warehouseService.interceptors.request.use(
        async (config) => {
          try {
            const token = await accessTokenFactory(
              stateAccessToken,
              authMethod
            );

            if (token) {
              // eslint-disable-next-line no-param-reassign
              config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
          } catch {
            // Proceed without attaching token if an error occurs
            return config;
          }
        },
        (error) => {
          // Reject the promise if there's an error setting up the interceptor
          return Promise.reject(error);
        }
      );

      resolve(true);
    } catch {
      resolve(false);
    }
  });
}

export function handleWarehouseError(
  err: unknown,
  dispatch: (message: string) => void
): void {
  if (isAxiosError<string | string[]>(err)) {
    const { message = "Something Went Wrong!" } = err;
    const responseMessage = err?.response?.data;
    const stringMessage = Array.isArray(responseMessage)
      ? processErrorArray(responseMessage)
      : responseMessage;
    dispatch(stringMessage || message);
  } else if (err instanceof Error) {
    const { message } = err;
    dispatch(message);
  }

  throw err;
}

export function handleWarehouseErrorAsyncThunk(err: unknown): string {
  if (isAxiosError<string | string[]>(err)) {
    const { message = "Something Went Wrong!" } = err;
    const responseMessage = err?.response?.data;
    const stringMessage = Array.isArray(responseMessage)
      ? processErrorArray(responseMessage)
      : responseMessage;
    return stringMessage || message;
  }
  if (err instanceof Error) {
    const { message } = err;
    return message;
  }

  throw err;
}
